import clsx from "clsx";
import {
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

interface Props {
  length?: number;
  value: string;
  setValue: (value: string) => void;
  error?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
}

export const OTPInput: FC<Props> = ({
  length = 6,
  value,
  setValue,
  error,
  autoFocus = false,
  disabled = false,
  className,
}) => {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(
    autoFocus ? 0 : null
  );
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = useCallback(
    (index: number, newValue: string) => {
      if (newValue && !/^\d$/.test(newValue)) {
        return;
      }

      const newCode = value.split("");
      newCode[index] = newValue;
      const updatedCode = newCode.join("").slice(0, length);
      setValue(updatedCode);

      if (newValue && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
        setFocusedIndex(index + 1);
      }
    },
    [value, length, setValue]
  );

  const handleKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && !value[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
        setFocusedIndex(index - 1);
      } else if (e.key === "ArrowLeft" && index > 0) {
        inputRefs.current[index - 1]?.focus();
        setFocusedIndex(index - 1);
      } else if (e.key === "ArrowRight" && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
        setFocusedIndex(index + 1);
      }
    },
    [value, length]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData("text").slice(0, length);
      if (/^\d+$/.test(pastedData)) {
        setValue(pastedData);
        const nextIndex = Math.min(pastedData.length, length - 1);
        inputRefs.current[nextIndex]?.focus();
        setFocusedIndex(nextIndex);
      }
    },
    [length, setValue]
  );

  const handleFocus = useCallback((index: number) => {
    setFocusedIndex(index);
    inputRefs.current[index]?.select();
  }, []);

  const handleBlur = useCallback(() => {
    setFocusedIndex(null);
  }, []);

  return (
    <div className={clsx("otp-input-container", className)}>
      {Array.from({ length }).map((_, index) => (
        <div
          key={index}
          className={clsx("otp-input-wrapper", {
            focused: focusedIndex === index,
            error: error,
            filled: value[index],
          })}
        >
          <input
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={value[index] || ""}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={() => handleFocus(index)}
            onBlur={handleBlur}
            disabled={disabled}
            className="otp-input"
            autoComplete="off"
          />
        </div>
      ))}
    </div>
  );
};
